import { Facebook, Instagram, Linkedin } from 'lucide-react';

/** Discord's mark. lucide ships no brand icons for it, so it is inlined. */
export function DiscordIcon({ size = 16, className = '' }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden className={className}>
      <path d="M20.32 4.37A19.8 19.8 0 0 0 15.43 2.85a.07.07 0 0 0-.08.04c-.21.38-.44.87-.61 1.25a18.3 18.3 0 0 0-5.49 0 12.6 12.6 0 0 0-.62-1.25.08.08 0 0 0-.08-.04 19.7 19.7 0 0 0-4.89 1.52.07.07 0 0 0-.03.03C.53 9.05-.32 13.58.1 18.06a.08.08 0 0 0 .03.06 19.9 19.9 0 0 0 6 3.03.08.08 0 0 0 .08-.03c.46-.63.87-1.3 1.23-1.99a.08.08 0 0 0-.04-.1 13.1 13.1 0 0 1-1.87-.9.08.08 0 0 1-.01-.13c.13-.09.25-.19.37-.29a.07.07 0 0 1 .08-.01c3.93 1.79 8.18 1.79 12.06 0a.07.07 0 0 1 .08.01c.12.1.25.2.37.29a.08.08 0 0 1-.01.13c-.6.35-1.22.65-1.87.9a.08.08 0 0 0-.04.1c.36.7.78 1.36 1.23 1.99a.08.08 0 0 0 .08.03 19.8 19.8 0 0 0 6-3.03.08.08 0 0 0 .03-.06c.5-5.18-.84-9.67-3.55-13.66a.06.06 0 0 0-.03-.03ZM8.02 15.33c-1.18 0-2.16-1.09-2.16-2.42 0-1.33.96-2.42 2.16-2.42 1.21 0 2.18 1.1 2.16 2.42 0 1.33-.96 2.42-2.16 2.42Zm7.97 0c-1.18 0-2.15-1.09-2.15-2.42 0-1.33.95-2.42 2.15-2.42 1.21 0 2.18 1.1 2.16 2.42 0 1.33-.95 2.42-2.16 2.42Z" />
    </svg>
  );
}

/** TikTok's mark, inlined for the same reason. */
export function TikTokIcon({ size = 16, className = '' }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden className={className}>
      <path d="M16.6 2h-3.4v13.3a2.9 2.9 0 1 1-2.9-2.9c.3 0 .6 0 .9.1V9.1a6.3 6.3 0 1 0 5.4 6.2V8.6a8.1 8.1 0 0 0 4.8 1.5V6.7a4.8 4.8 0 0 1-4.8-4.7Z" />
    </svg>
  );
}

type IconComponent = (p: { size?: number; className?: string }) => JSX.Element | null;

/** The club's real community links, in display order. Edited here only. */
export const SOCIAL: ReadonlyArray<{ key: string; href: string; label: string; Icon: IconComponent }> = [
  { key: 'facebook', href: 'https://www.facebook.com/hbac.bkhn', label: 'Fanpage HBAC', Icon: Facebook as IconComponent },
  { key: 'instagram', href: 'https://www.instagram.com/humansofhbac', label: 'Instagram HBAC', Icon: Instagram as IconComponent },
  { key: 'tiktok', href: 'https://www.tiktok.com/@hbac.2021', label: 'TikTok HBAC', Icon: TikTokIcon },
  { key: 'linkedin', href: 'https://www.linkedin.com/company/hust-business-analytics-club-hbac', label: 'LinkedIn HBAC', Icon: Linkedin as IconComponent },
  { key: 'discord', href: 'https://discord.gg/jRRy5eHskw', label: 'Discord HBAC', Icon: DiscordIcon },
];

export const CLUB_EMAIL = 'hustbusinessanalyticsclub@gmail.com';

const external = { target: '_blank', rel: 'noreferrer noopener' } as const;

/** Full class strings per surface: Tailwind cannot see built ones. */
const VARIANT = {
  dark: 'bg-white/10 text-white ring-1 ring-white/20 hover:bg-white/20 focus-visible:ring-2 focus-visible:ring-white/60',
  light:
    'bg-white text-slate-500 ring-1 ring-slate-200 hover:text-brand-700 hover:ring-brand-300 focus-visible:ring-2 focus-visible:ring-brand-600 dark:focus-visible:ring-brand-400 dark:bg-navy-900 dark:text-slate-300 dark:ring-navy-700 dark:hover:text-brand-300 dark:hover:ring-brand-500/40',
} as const;

/** Icon-only links. The name lives in aria-label and title. */
export function SocialButtons({ variant = 'dark', testIdPrefix = 'social' }: { variant?: keyof typeof VARIANT; testIdPrefix?: string }) {
  return (
    <div className="flex flex-wrap gap-2">
      {SOCIAL.map(({ key, href, label, Icon }) => (
        <a
          key={key}
          href={href}
          {...external}
          aria-label={label}
          title={label}
          data-testid={`${testIdPrefix}-${key}`}
          className={`inline-flex h-10 w-10 items-center justify-center rounded-xl transition-colors focus-visible:outline-none ${VARIANT[variant]}`}
        >
          <Icon size={18} />
        </a>
      ))}
    </div>
  );
}
